export const buildAgentInstructions = (baseUrl, apiKey) => `# WholesaleOffers CRM — AI Agent Access

You have full API access to my real estate wholesaling CRM. Use it to manage leads, buyers, contracts and emails on my behalf.

## Connection
- Base URL: ${baseUrl}/api
- Auth header (send on EVERY request): X-API-Key: ${apiKey}
- Content-Type: application/json
- All IDs are string UUIDs. Errors return {"detail": "..."}.

## Leads (properties / deals)
- GET    /api/leads                 List leads. Query params: status, search, property_type, sort_by, sort_order
- POST   /api/leads                 Create a lead ("name" required). Returns 409 if the property_address already exists
- GET    /api/leads/{lead_id}       Get one lead
- PUT    /api/leads/{lead_id}       Update any subset of fields (send only what changes)
- DELETE /api/leads/{lead_id}       Delete a lead
- GET    /api/leads/{lead_id}/activities   Activity timeline
- POST   /api/leads/{lead_id}/activities   Add a note: {"activity_type": "note", "description": "..."}
- POST   /api/leads/import-csv      Bulk import (multipart form, field "file")

Lead stages (status): New, Offer Sent, Follow-Up, Under Contract, Marketed, Sold, Closed, Dead, Cancelled

Lead fields:
- Property: property_address, property_city, property_state, property_zip, county, property_type, bedrooms (int), bathrooms (int), sqft (int), folio, legal_description
- Pricing: asking_price, offer_amount, arv, repair_estimate, assignment_fee, marketing_price, under_contract_price, sold_price (numbers)
- Contract terms: escrow_deposit, closing_date (YYYY-MM-DD), inspection_time (e.g. "10 days"), inspection_period_days (int), assignability, closing_costs, additional_terms, date_signed
- People: name, seller_name, agent_name, agent_email, agent_phone, agent_company, listing_broker, buyer_name, buyer_email, buyer_phone
- Other: notes

Example — create a lead:
POST /api/leads
{"name": "123 Main St Deal", "property_address": "123 Main St", "property_city": "Miami", "property_state": "FL", "property_zip": "33101", "asking_price": 250000, "offer_amount": 200000, "agent_name": "Jane Doe", "agent_email": "jane@realty.com"}

## Contracts (PDF generation)
Fill all contract-related lead fields first (PUT /api/leads/{id}) — templates auto-fill from lead data.
- GET    /api/templates             List contract templates (get template_id here)
- POST   /api/contracts/generate    {"lead_id": "...", "template_id": "...", "field_values": {}}  → returns contract with "id"
- GET    /api/contracts?lead_id=... List generated contracts
- GET    /api/contracts/{contract_id}/download   Download PDF
- DELETE /api/contracts/{contract_id}

## Emails
- GET  /api/email-templates         List saved email templates (subject + html_content)
- POST /api/emails/send             Send an offer email to the listing agent, with contracts attached:
  {"lead_id": "...", "to_email": "agent@x.com", "subject": "...", "html_content": "...", "contract_ids": ["..."], "send_copy": true}
  (A "New" lead automatically moves to "Offer Sent".)
- POST /api/emails/send-blast       Deal blast to many recipients:
  {"lead_id": "...", "subject": "...", "html_content": "...", "recipient_emails": ["a@x.com"], "recipient_type": "buyers"}
- GET  /api/leads/{lead_id}/blast-history
- GET  /api/emails                  Sent email log

## Cash Buyers (entities + contacts + purchases)
- GET    /api/buyers?page=1&page_size=50 + filters → {items,total,page,pages}. Filters: q, county, market_tab, tier (Hot|Active|Cold), strategy, property_type (SFR|Townhouse|Condo|2-4 Unit|5+), city, zip (comma lists), min_count_in_zip, zip_preference, call_status, has_mobile, not_dnc, sms_opt_in, tags, price_min, price_max, beds_min, baths_min, last_purchase_within_days, last_purchase_after. sort_by: total_purchases_24mo | purchases_12mo | last_purchase_date | last_purchase_price | buyer_score | tier | avg_price | mf_kept_count | mf_flipped_count | location_match_count | entity_name; sort_order asc|desc
- GET    /api/buyers/stats · GET /api/buyers/export (CSV, same filters) · GET /api/buyers/import-template
- POST   /api/buyers/bulk-upsert  JSON array: [{"entity_name","sunbiz_doc_number","county","market_tab","tags":[],"contacts":[{"first_name","last_name","phones":[{"number","type":"mobile","dnc":false}],"emails":[]}],"purchases":[{"property_address","city","zip","folio","property_type","beds","baths","sqft","purchase_price","purchase_date","outcome"}]}] → {created,updated,errors,buyer_ids}. Matches by Sunbiz Doc # then entity name; never deletes
- POST   /api/buyers/import (multipart "file" CSV/XLSX, optional "market_tab") → {job_id}; GET /api/buyers/import/{job_id} for status
- GET/PUT/DELETE /api/buyers/{id} · PUT /api/buyers/{id}/profile · POST /api/buyers/{id}/tags {"add":[],"remove":[]} · PUT /api/buyers/{id}/status {"status"}
- GET/POST /api/buyers/{id}/contacts · PUT/DELETE /api/buyers/{id}/contacts/{contact_id} (call_status, sms_opt_in, litigator, phones, emails)
- GET/POST /api/buyers/{id}/purchases · PUT/DELETE /api/buyers/{id}/purchases/{purchase_id}
- GET /api/buyers/{id}/activity · POST /api/buyers/{id}/notes {"text"}
- Segments: GET/POST /api/buyer-segments {"name","filters":{...}} · PUT/DELETE /api/buyer-segments/{id} · GET /api/buyer-segments/{id}/buyers
- GET /api/leads/{lead_id}/matched-buyers?limit=50 (ranked 0-100 with reasons: zip/city buys, price range, beds/baths/sqft, type, recency, tier)
- POST /api/buyers/dedupe-preview · POST /api/buyers/merge {"primary_id","merge_ids":[]}

## Buyer Campaigns (email + compliant SMS)
- POST /api/campaigns {"channel":"email"|"sms","name","subject","body" (or "template_id"),"lead_id","recipients":{"segment_id"|"buyer_ids":[]|"filters":{"tier","zip","city"}|"lead_match":{"lead_id","top_n"}},"schedule_at"} → campaigns created via API are always "draft"; the owner approves them in the app
- Merge fields: {first_name} {entity_name} {property_address} {price} {beds} {baths} {sqft} {arv} {photos_link}
- POST /api/campaigns/preview-recipients (same body) → eligible count + exclusions
- GET /api/campaigns · GET /api/campaigns/{id} · GET /api/campaigns/{id}/stats (sent, opened, clicked, unsubscribed, failed, texted) · GET /api/campaigns/{id}/sends
- PUT /api/campaigns/{id} (draft/scheduled) · POST /api/campaigns/{id}/cancel · DELETE /api/campaigns/{id}
- Gmail reply/bounce sync: POST /api/gmail-sync/run · GET /api/gmail-sync/status
- Replies: POST /api/buyer-replies {"buyer_id"|"from_address","channel","text"} (STOP auto opts out) · GET /api/buyer-replies?since=ISO
- GET/POST /api/email-suppressions {"email","reason"}

## Wholesalers
- GET/POST /api/wholesalers, GET/PUT/DELETE /api/wholesalers/{id}

## Drip Campaigns
- GET  /api/drip-campaigns          List campaigns
- POST /api/drip-assignments        Enroll: {"campaign_id": "...", "target_type": "lead" | "buyer", "target_id": "..."}
- GET  /api/drip-assignments        List enrollments

## Dashboard & Account
- GET /api/dashboard/stats          Pipeline stats
- GET /api/earnings                 Earnings summary
- GET /api/user/profile             My profile

## Typical workflow — make an offer
1. POST /api/leads with property + agent info (or GET /api/leads?search=address to find an existing one)
2. PUT /api/leads/{id} with offer_amount, escrow_deposit, closing_date, inspection_time, seller_name, etc.
3. GET /api/templates → POST /api/contracts/generate for each template needed
4. POST /api/emails/send to agent_email with contract_ids attached
`;
